/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import csvSensorMuensterDemo from '@/config/data/ee_check_demo/sensors.muenster.csv?raw';
import csvSensorMuenster from '@/config/data/ee_check/sensors.muenster.csv?raw';
import csvSensorNeutraubling from '@/config/data/ee_check/sensors.neutraubling.csv?raw';

/* Import libraries */
import Papa from 'papaparse';

import { readStoredSessionUsername } from '@/lib/app/sessionState';

export const DEMO_CSV_USERNAME = 'ewdemo';
export const DEMO_CSV_USERNAMES = [DEMO_CSV_USERNAME, 'saskarzadeh'] as const;

/** Keeps all restricted-demo behavior consistent across supported demo accounts. */
export function isDemoUser(username: string): boolean {
    return DEMO_CSV_USERNAMES.some((demoUsername) => demoUsername === username);
}

const DEMO_CSV_SYSTEMS_FLAGS = {
    common: true,
    interesting: true,

    common_disabled: false,
    interesting_disabled: false,
};

/**
 * Systems available to the restricted demonstration account.
 * Add optional metadata per Münster system here to adjust the demo data scope.
 */
export const DEMO_CSV_SYSTEMS = {
    'Mue.0005':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.interesting_disabled, proved: true,  comment: 'Sehr gute Wochenend-Absenkung.' },
    'Mue.0022':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.interesting_disabled, proved: true,  comment: 'Sehr gute Wochenend-Absenkung.' },
    'Mue.0027':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0048':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0054':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0059':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0063':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.interesting_disabled, proved: true,  comment: 'Mit PV Anlage. Sehr gute Wochenend-Absenkung.' },
    'Mue.0065':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0066':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0068':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common,               proved: true,  comment: 'Hohe Grundlast. Hoher Wasserverlust die letzten 7 Tage. Offenbar gefixt.' },
    'Mue.0072':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0080':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.interesting_disabled, proved: true,  comment: 'Mittelmäßige Wochenend-Absenkung.' },
    'Mue.0084':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0088':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0090':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0098':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0099':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common,               proved: true,  comment: '' },
    'Mue.0116':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common,               proved: true,  comment: 'Sehr hoher Wasserverlust. Grundlast ok.' },
    'Mue.0118':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0120':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0122':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0131':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common,               proved: true,  comment: 'Hoher Wasserverlust. Grundlast ok.' },
    'Mue.0134':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0144':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0148':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common,               proved: true,  comment: 'Keine Wochenend-Absenkung. Sehr hoher Wasserverlust.' },
    'Mue.0161':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0163':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common,               proved: true,  comment: 'Sehr gute Wochenend-Absenkung. Wenig Wasserverlust.' },
    'Mue.0176':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0182':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common,               proved: true,  comment: 'Sehr gute Wochenend-Absenkung. Wenig Wasserverlust.' },
    'Mue.0190':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0194':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0196':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0197':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0211':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common,               proved: true,  comment: '' },
    'Mue.0227':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0229':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0232':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0246':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0254':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0262':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common,               proved: true,  comment: 'Sehr gute Wochenend-Absenkung. Hoher Wasserverlust innerhalb letzter 7 Tage. Jetzt ok.' },
    'Mue.0264':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0276':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common,               proved: true,  comment: 'Sehr gute Wochenend-Absenkung. Wenig Wasserverlust.' },
    'Mue.0296':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0301':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0310':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0312':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0313':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common,               proved: true,  comment: 'Hohe Grundlast die letzten 2 Tage. Sehr gute Wochenend-Absenkung. Hoher Wasserverlust. Offenbar gefixt die letzten Tage.' },
    'Mue.0316':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0317':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common,               proved: true,  comment: 'Immer mal hohe Grundlast. Sehr gute Wochenend-Absenkung. Wenig Wasserverlust.' },
    'Mue.0342':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0382':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0382a':   { enabled: DEMO_CSV_SYSTEMS_FLAGS.common,               proved: true,  comment: '' },
    'Mue.0382b':   { enabled: DEMO_CSV_SYSTEMS_FLAGS.common,               proved: true,  comment: '' },
    'Mue.0385':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0387':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.interesting_disabled, proved: true,  comment: 'Mit PV Anlage. Sehr hohe Gesamtkosten beim Strom.' },
    'Mue.0389':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0390':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0395':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0417':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common,               proved: true,  comment: 'Gute Grundlast aktuell. Schlechte Wochenend-Absenkung. Wenig Wasserverlust.' },
    'Mue.0423':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0424':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0430':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0434':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0442':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0443':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0444':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0446':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0448':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0459':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0465':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0469':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0479':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0494':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0501':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0505':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0507':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0509':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0515':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0520':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common,               proved: true,  comment: 'Hohe Grundlast. Sehr gute Wochenend-Absenkung. Kein Wasserverlust.' },
    'Mue.0528':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0531':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.interesting,          proved: true,  comment: 'PV Anlage mit Ausfall. Sehr gute Wochenend-Absenkung. Sehr gute BaseLoad Einstellung. Kein Wasserverlust.' },
    'Mue.0536':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common,               proved: true,  comment: 'Teils hohe Grundlast. Schlechte Wochenend-Absenkung (Turnhalle). Kein Wasserverlust.' },
    'Mue.0537':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0554':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0575':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0590':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0609':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0622':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0623':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0625':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.interesting_disabled, proved: true,  comment: 'Mit PV Anlage. Gute Wochenend-Absenkung.' },
    'Mue.0626':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0678':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0701':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0707':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0708':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0721':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0730':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0739':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0745':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0751':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0752':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0766':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.interesting,          proved: true,  comment: 'Kleiner Ausfall der PV Anlage. Sehr hohe Gesamtkosten beim Strom. Hohe Grundlast. Geringer Wasserverlust.' },
    'Mue.0777':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common,               proved: true,  comment: 'Sehr gute Grundlast. Mittlerer Wasserverlust. Sehr gute Wochenend-Absenkung.' },
    'Mue.0778':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0783':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0785':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0786':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0798':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0819':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common,               proved: true,  comment: 'Sehr hohe Grundlast. Kein Wasserverlust. Sehr schlechte Wochenend-Absenkung (Turnhalle).' },
    'Mue.0823':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0846':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0882':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common,               proved: true,  comment: '' },
    'Mue.0887':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0924':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0924a':   { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0924b':   { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.0936':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common,               proved: true,  comment: '' },
    'Mue.0945alt': { enabled: DEMO_CSV_SYSTEMS_FLAGS.common,               proved: true,  comment: '' },
    'Mue.0985':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.interesting,          proved: true,  comment: 'PV Anlage mit Ausfall. Sehr Grundlast die letzten Tage. Sehr hoher Wasserverlust.' },
    'Mue.1266':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.common_disabled,      proved: true,  comment: '' },
    'Mue.1443':    { enabled: DEMO_CSV_SYSTEMS_FLAGS.interesting_disabled, proved: true,  comment: 'Mit PV Anlage.' },
} as const;

interface DemoCsvSystemConfig {
    readonly enabled: boolean;
}

export function getEnabledDemoCsvSystemKeys(
    systems: Record<string, DemoCsvSystemConfig> = DEMO_CSV_SYSTEMS,
): string[] {
    return Object.entries(systems)
        .filter(([, config]) => config.enabled)
        .map(([system]) => system);
}

export interface CsvSensorSources {
    muenster: string;
    neutraubling: string | null;
}

export function filterCsvSensorsBySystem(
    csv: string,
    systems: readonly string[],
): string {
    const parsedCsv = Papa.parse<string[]>(csv.trim(), {
        delimiter: ';',
        skipEmptyLines: true,
    });
    const [header = [], ...rows] = parsedCsv.data;
    const systemColumnIndex = header.findIndex((column) => column.trim() === 'System');

    if (systemColumnIndex === -1) {
        return csv.trim();
    }

    const allowedSystems = new Set(systems);
    const filteredRows = rows.filter((row) => allowedSystems.has(row[systemColumnIndex]?.trim() ?? ''));

    return Papa.unparse([header, ...filteredRows], {
        delimiter: ';',
        newline: '\n',
    }).trim();
}

export function getCsvSensorSources(username: string): CsvSensorSources {
    if (isDemoUser(username)) {
        return {
            muenster: filterCsvSensorsBySystem(
                csvSensorMuensterDemo,
                getEnabledDemoCsvSystemKeys(),
            ),
            neutraubling: null,
        };
    }

    return {
        muenster: csvSensorMuenster.trim(),
        neutraubling: csvSensorNeutraubling.trim(),
    };
}

const storedSessionUsername = readStoredSessionUsername();

/** True when the stored login belongs to a restricted demonstration account. */
export let IS_DEMO_USER = isDemoUser(storedSessionUsername);

let activeCsvSensorSources = getCsvSensorSources(storedSessionUsername);

export let CSV_SENSOR_MUENSTER = activeCsvSensorSources.muenster;
export let CSV_SENSOR_NEUTRAUBLING = activeCsvSensorSources.neutraubling;
export let HAS_NEUTRAUBLING_SENSOR_DATA = CSV_SENSOR_NEUTRAUBLING !== null;

/** Rebuilds the user-scoped CSV sources after an in-app login or logout. */
export function refreshCsvSensorCatalog(username: string): void {
    IS_DEMO_USER = isDemoUser(username);
    activeCsvSensorSources = getCsvSensorSources(username);
    CSV_SENSOR_MUENSTER = activeCsvSensorSources.muenster;
    CSV_SENSOR_NEUTRAUBLING = activeCsvSensorSources.neutraubling;
    HAS_NEUTRAUBLING_SENSOR_DATA = CSV_SENSOR_NEUTRAUBLING !== null;
}
